// src/pages/document/DocumentVersionListPage.tsx
// Version history for a specific document.

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useDocument, useI18n } from '@react/hooks';
import {
  Card,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  ErrorAlert,
  Badge,
} from '@react/components';
import { useScope } from '@/hooks/useScope';
import {
  documentStatusMap,
  getStatusMapping,
  formatDate,
} from '@/features/document/logic';

export const DocumentVersionListPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useDocument();
  const scope = useScope();
  const { t, locale } = useI18n();
  const [items, setItems] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [docTitle, setDocTitle] = useState('');

  // Load document title for breadcrumb
  useEffect(() => {
    if (!id) return;
    store.GetDocument(id).then((doc: any) => {
      setDocTitle(doc?.title || '');
    }).catch(() => {});
  }, [id, scope.productId]);

  // Load versions for this specific document
  useEffect(() => {
    if (!id) return;
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await store.ListVersions('document', id);
        // Filter to only versions belonging to this document (safety net)
        const allVersions = res?.versions?.versions || [];
        const filtered = allVersions.filter((v: any) => {
          const targetId = v?.target?.id || v?.document_id;
          return !targetId || targetId === id;
        });
        setItems(filtered);
      } catch (err: unknown) {
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [id]);

  return (
    <div className="space-y-6">
      {/* Page header — the tab already names this page, so the heading is screen-reader only. */}
      <div>
        <h1 className="sr-only">{t('document.versionHistory')}</h1>
        <nav className="text-sm">
          <Link to="/documents" className="text-muted-foreground hover:text-foreground">
            {t('document.list')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <Link to={`/documents/${id}`} className="text-muted-foreground hover:text-foreground">
            {docTitle || t('document.details')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('document.versionHistory')}</span>
        </nav>
      </div>

      {error ? (
        <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} />
      ) : null}

      <Card>
        <Table hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>{t('document.version.number')}</TableHead>
              <TableHead>{t('document.version.changeSummary')}</TableHead>
              <TableHead>{t('document.fieldStatus')}</TableHead>
              <TableHead>{t('document.fieldCreatedAt')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8">
                  {t('common.loading')}
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableEmpty colSpan={4} message={t('document.version.noVersions')} />
            ) : (
              items.map((item: any) => {
                const versionId = item.entity?.id || item.id;
                const status = item.status || '';
                const statusInfo = getStatusMapping(documentStatusMap, status);

                return (
                  <TableRow key={versionId || Math.random()}>
                    {/* Version number */}
                    <TableCell>
                      <Link
                        to={`/documents/${id}/versions/${versionId}`}
                        className="text-sm font-medium text-primary hover:underline whitespace-nowrap"
                      >
                        {t('document.version.number')} {item.version_number ?? '—'}
                      </Link>
                      {item.version_label && <div className="text-xs text-muted-foreground mt-1">{item.version_label}</div>}
                    </TableCell>

                    {/* Change summary */}
                    <TableCell>
                      <span className="text-sm text-muted-foreground break-words max-w-lg block">
                        {item.change_summary || t('document.versions.noSummary')}
                      </span>
                    </TableCell>
                    <TableCell>
                      {status && <Badge variant={statusInfo.variant as any} size="sm">{t(statusInfo.labelKey)}</Badge>}
                    </TableCell>

                    {/* Created at */}
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {formatDate(item.created_at, locale)}
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
});

export default DocumentVersionListPage;
